/**
 * Voice / telephony domain events — catalog v10 additions (commerce-platform
 * Phase 6, plan §5 "Event catalog v10" + spec voice-cr-spike-report A5).
 *
 * Emitted by `services/voice-bridge` (the Twilio Conversation Relay transport)
 * alongside its `voice_sessions` Mongo store (plan P6-A3). Fan-out room:
 * `ROOM_VOICE_UPDATES` (`voice:updates`), ORG-SCOPED per master A13 — the
 * gateway bridge appends `:${payload.orgId}` (see room-registry.ts and
 * ws-cluster-events-bridge.service.ts).
 *
 * - `voice:call-started`      — a call leg was accepted and a session opened
 *   (direction inbound|outbound; unknown caller => guest, no customerId).
 * - `voice:caller-identified` — caller resolved against customer profiles
 *   (by-phone lookup) or a voice-declared membership number.
 * - `voice:approval-readout`  — an in-call approval request was read out to
 *   the caller and its status polled/reported (plan §5 approvals-voice).
 * - `voice:escalated`         — handoff to a human: the CR socket closed and
 *   the TwiML fell through to <Dial> (reason enum below).
 * - `voice:call-ended`        — the session reached a terminal state.
 *
 * CALLER PII BOUNDARY (plan §9 — hard gate): payloads NEVER contain a raw
 * phone number. `phoneHash`/`fromHash`/`toHash` are the SHA-256 hex digest of
 * the normalized E.164 value, mirroring the `agent:audio-transcript` redaction
 * precedent in room-registry.ts (raw transcript content excluded from fan-out)
 * and the hashed-credential precedent in catalog/identity.ts. Schemas reject
 * any hash field that is not exactly 64 lowercase hex chars, so an accidental
 * raw-number pass-through fails at publish time instead of leaking to rooms.
 * Call audio is never retained or referenced (voice-bridge records default
 * off per plan P6-A2).
 *
 * NOTE (catalog release naming, mirrors drift note R-5 in catalog/email.ts):
 * the phase plan text says "catalog v10" — the actual latest catalog release
 * today is 1.12.0 (continuous tracks T06 Part A: `EMAIL_MAILBOX_EVENTS`), so
 * these definitions land in the next real release, `since: '1.13.0'`.
 */
import {
  asEventRecord,
  defineEvent,
  optionalNumber,
  optionalString,
  requireString,
  type EventDefinition,
  type EventSchema,
} from './registry';

const DIRECTION_VALUES = ['inbound', 'outbound'] as const;
const SESSION_STATUS_VALUES = ['ringing', 'active', 'escalated', 'ended'] as const;
const IDENTIFIED_BY_VALUES = ['phone-lookup', 'declared-membership'] as const;
const ESCALATION_REASON_VALUES = [
  'agent_intent',
  'verdict_escalate',
  'approval_expired',
  'caller_request',
] as const;
const APPROVAL_STATUS_VALUES = ['pending', 'approved', 'rejected', 'expired'] as const;

/** SHA-256 hex digest of the normalized E.164 caller number — never the number. */
const PHONE_HASH_PATTERN = /^[0-9a-f]{64}$/;

export interface VoiceCallStartedPayload extends Record<string, unknown> {
  callSid: string;
  orgId: string;
  direction: string;
  fromHash?: string;
  toHash?: string;
  customerId?: string;
  agentRunId?: string;
  startedAt: string;
}

export interface VoiceCallerIdentifiedPayload extends Record<string, unknown> {
  callSid: string;
  orgId: string;
  phoneHash: string;
  identifiedBy: string;
  customerId?: string;
  identifiedAt: string;
}

export interface VoiceApprovalReadoutPayload extends Record<string, unknown> {
  callSid: string;
  orgId: string;
  approvalId: string;
  approvalStatus: string;
  readoutAt: string;
  approverUserId?: string;
}

export interface VoiceEscalatedPayload extends Record<string, unknown> {
  callSid: string;
  orgId: string;
  reason: string;
  escalatedAt: string;
  agentRunId?: string;
  approvalId?: string;
  decisionId?: string;
}

export interface VoiceCallEndedPayload extends Record<string, unknown> {
  callSid: string;
  orgId: string;
  status: string;
  endedAt: string;
  durationSeconds?: number;
  escalationReason?: string;
}

function requireEnumValue(
  value: unknown,
  field: string,
  allowed: readonly string[],
  context: string,
): string {
  if (typeof value !== 'string' || !allowed.includes(value)) {
    throw new Error(`${context}: field "${field}" must be one of ${allowed.join(', ')}`);
  }
  return value;
}

/**
 * Required phone-hash field: 64 lowercase hex chars only. A raw E.164 number
 * (`+15551234567`) can never satisfy this, which is the point — the check is
 * the PII gate, not decoration.
 */
function requirePhoneHash(value: unknown, field: string, context: string): string {
  if (typeof value !== 'string' || !PHONE_HASH_PATTERN.test(value)) {
    throw new Error(
      `${context}: field "${field}" must be a SHA-256 hex digest of the normalized E.164 number (raw phone numbers are never carried in event payloads)`,
    );
  }
  return value;
}

function optionalPhoneHash(value: unknown, field: string, context: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  return requirePhoneHash(value, field, context);
}

function optionalEnumValue(
  value: unknown,
  field: string,
  allowed: readonly string[],
  context: string,
): string | undefined {
  if (value === undefined || value === null) return undefined;
  return requireEnumValue(value, field, allowed, context);
}

// ---------------------------------------------------------------------------
// voice:call-started
// ---------------------------------------------------------------------------

const CALL_STARTED_TYPE = 'voice:call-started';

const callStartedSchema: EventSchema<VoiceCallStartedPayload> = {
  parse(data) {
    const record = asEventRecord(data, CALL_STARTED_TYPE);
    const fromHash = optionalPhoneHash(record.fromHash, 'fromHash', CALL_STARTED_TYPE);
    const toHash = optionalPhoneHash(record.toHash, 'toHash', CALL_STARTED_TYPE);
    const customerId = optionalString(record.customerId, 'customerId', CALL_STARTED_TYPE);
    const agentRunId = optionalString(record.agentRunId, 'agentRunId', CALL_STARTED_TYPE);
    return {
      callSid: requireString(record.callSid, 'callSid', CALL_STARTED_TYPE),
      orgId: requireString(record.orgId, 'orgId', CALL_STARTED_TYPE),
      direction: requireEnumValue(record.direction, 'direction', DIRECTION_VALUES, CALL_STARTED_TYPE),
      ...(fromHash === undefined ? {} : { fromHash }),
      ...(toHash === undefined ? {} : { toHash }),
      ...(customerId === undefined ? {} : { customerId }),
      ...(agentRunId === undefined ? {} : { agentRunId }),
      startedAt: requireString(record.startedAt, 'startedAt', CALL_STARTED_TYPE),
    };
  },
};

export const VOICE_CALL_STARTED_EVENT = defineEvent<VoiceCallStartedPayload>({
  type: CALL_STARTED_TYPE,
  version: 1,
  schema: callStartedSchema,
  auditable: true,
  description: 'A voice call leg was accepted and a voice_sessions row opened (numbers are SHA-256 hashes; unknown callers open a guest session with no customerId).',
  since: '1.13.0',
});

// ---------------------------------------------------------------------------
// voice:caller-identified
// ---------------------------------------------------------------------------

const CALLER_IDENTIFIED_TYPE = 'voice:caller-identified';

const callerIdentifiedSchema: EventSchema<VoiceCallerIdentifiedPayload> = {
  parse(data) {
    const record = asEventRecord(data, CALLER_IDENTIFIED_TYPE);
    const customerId = optionalString(record.customerId, 'customerId', CALLER_IDENTIFIED_TYPE);
    return {
      callSid: requireString(record.callSid, 'callSid', CALLER_IDENTIFIED_TYPE),
      orgId: requireString(record.orgId, 'orgId', CALLER_IDENTIFIED_TYPE),
      phoneHash: requirePhoneHash(record.phoneHash, 'phoneHash', CALLER_IDENTIFIED_TYPE),
      identifiedBy: requireEnumValue(
        record.identifiedBy,
        'identifiedBy',
        IDENTIFIED_BY_VALUES,
        CALLER_IDENTIFIED_TYPE,
      ),
      ...(customerId === undefined ? {} : { customerId }),
      identifiedAt: requireString(record.identifiedAt, 'identifiedAt', CALLER_IDENTIFIED_TYPE),
    };
  },
};

export const VOICE_CALLER_IDENTIFIED_EVENT = defineEvent<VoiceCallerIdentifiedPayload>({
  type: CALLER_IDENTIFIED_TYPE,
  version: 1,
  schema: callerIdentifiedSchema,
  auditable: true,
  description: 'A caller was resolved against customer profiles (per-org by-phone lookup or a voice-declared membership number); the payload carries the phone hash only.',
  since: '1.13.0',
});

// ---------------------------------------------------------------------------
// voice:approval-readout
// ---------------------------------------------------------------------------

const APPROVAL_READOUT_TYPE = 'voice:approval-readout';

const approvalReadoutSchema: EventSchema<VoiceApprovalReadoutPayload> = {
  parse(data) {
    const record = asEventRecord(data, APPROVAL_READOUT_TYPE);
    const approverUserId = optionalString(
      record.approverUserId,
      'approverUserId',
      APPROVAL_READOUT_TYPE,
    );
    return {
      callSid: requireString(record.callSid, 'callSid', APPROVAL_READOUT_TYPE),
      orgId: requireString(record.orgId, 'orgId', APPROVAL_READOUT_TYPE),
      approvalId: requireString(record.approvalId, 'approvalId', APPROVAL_READOUT_TYPE),
      approvalStatus: requireEnumValue(
        record.approvalStatus,
        'approvalStatus',
        APPROVAL_STATUS_VALUES,
        APPROVAL_READOUT_TYPE,
      ),
      readoutAt: requireString(record.readoutAt, 'readoutAt', APPROVAL_READOUT_TYPE),
      ...(approverUserId === undefined ? {} : { approverUserId }),
    };
  },
};

export const VOICE_APPROVAL_READOUT_EVENT = defineEvent<VoiceApprovalReadoutPayload>({
  type: APPROVAL_READOUT_TYPE,
  version: 1,
  schema: approvalReadoutSchema,
  auditable: true,
  description: 'An in-call approval request was read out to the caller with its current status (plan §5 approvals-voice; auditable so the discount approval chain is reconstructable).',
  since: '1.13.0',
});

// ---------------------------------------------------------------------------
// voice:escalated
// ---------------------------------------------------------------------------

const ESCALATED_TYPE = 'voice:escalated';

const escalatedSchema: EventSchema<VoiceEscalatedPayload> = {
  parse(data) {
    const record = asEventRecord(data, ESCALATED_TYPE);
    const agentRunId = optionalString(record.agentRunId, 'agentRunId', ESCALATED_TYPE);
    const approvalId = optionalString(record.approvalId, 'approvalId', ESCALATED_TYPE);
    const decisionId = optionalString(record.decisionId, 'decisionId', ESCALATED_TYPE);
    return {
      callSid: requireString(record.callSid, 'callSid', ESCALATED_TYPE),
      orgId: requireString(record.orgId, 'orgId', ESCALATED_TYPE),
      reason: requireEnumValue(record.reason, 'reason', ESCALATION_REASON_VALUES, ESCALATED_TYPE),
      escalatedAt: requireString(record.escalatedAt, 'escalatedAt', ESCALATED_TYPE),
      ...(agentRunId === undefined ? {} : { agentRunId }),
      ...(approvalId === undefined ? {} : { approvalId }),
      ...(decisionId === undefined ? {} : { decisionId }),
    };
  },
};

export const VOICE_ESCALATED_EVENT = defineEvent<VoiceEscalatedPayload>({
  type: ESCALATED_TYPE,
  version: 1,
  schema: escalatedSchema,
  auditable: true,
  description: 'A call was handed off to a human queue: the Conversation Relay socket closed and the TwiML fell through to <Dial> (agent intent, escalate_to_human verdict, approval expiry, or caller request).',
  since: '1.13.0',
});

// ---------------------------------------------------------------------------
// voice:call-ended
// ---------------------------------------------------------------------------

const CALL_ENDED_TYPE = 'voice:call-ended';

const callEndedSchema: EventSchema<VoiceCallEndedPayload> = {
  parse(data) {
    const record = asEventRecord(data, CALL_ENDED_TYPE);
    const durationSeconds = optionalNumber(
      record.durationSeconds,
      'durationSeconds',
      CALL_ENDED_TYPE,
    );
    const escalationReason = optionalEnumValue(
      record.escalationReason,
      'escalationReason',
      ESCALATION_REASON_VALUES,
      CALL_ENDED_TYPE,
    );
    return {
      callSid: requireString(record.callSid, 'callSid', CALL_ENDED_TYPE),
      orgId: requireString(record.orgId, 'orgId', CALL_ENDED_TYPE),
      status: requireEnumValue(record.status, 'status', SESSION_STATUS_VALUES, CALL_ENDED_TYPE),
      endedAt: requireString(record.endedAt, 'endedAt', CALL_ENDED_TYPE),
      ...(durationSeconds === undefined ? {} : { durationSeconds }),
      ...(escalationReason === undefined ? {} : { escalationReason }),
    };
  },
};

export const VOICE_CALL_ENDED_EVENT = defineEvent<VoiceCallEndedPayload>({
  type: CALL_ENDED_TYPE,
  version: 1,
  schema: callEndedSchema,
  auditable: true,
  description: 'A voice session reached a terminal state (ended|escalated) with its final status; no call audio is retained or referenced.',
  since: '1.13.0',
});

export const VOICE_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [VOICE_CALL_STARTED_EVENT.type]: VOICE_CALL_STARTED_EVENT,
  [VOICE_CALLER_IDENTIFIED_EVENT.type]: VOICE_CALLER_IDENTIFIED_EVENT,
  [VOICE_APPROVAL_READOUT_EVENT.type]: VOICE_APPROVAL_READOUT_EVENT,
  [VOICE_ESCALATED_EVENT.type]: VOICE_ESCALATED_EVENT,
  [VOICE_CALL_ENDED_EVENT.type]: VOICE_CALL_ENDED_EVENT,
};
